import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import styles from './MediaView.module.scss'
import {
  GET_MEDIA_LIBRARY_QUERY,
  GET_PROJECTS_QUERY,
  ATTACH_MEDIA_ASSETS_MUTATION,
  CREATE_UPLOAD_GRANT_MUTATION,
} from '../query/gqlQueries'
import { uploadFile, runIngest, CHUNK_THRESHOLD, INBOX_PATH } from '../uploadMedia'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'
import VideocamIcon from '@mui/icons-material/Videocam'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import CameraIcon from '@mui/icons-material/Camera'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack'
import FlightIcon from '@mui/icons-material/Flight'
import DevicesOtherIcon from '@mui/icons-material/DevicesOther'

// The devices media can be imported from. Fixed rather than free text: a typed
// name drifts ("Sony" / "sony" / "a7iv") and every spelling becomes its own
// folder under _library/<date>/ and its own group in the listing.
//
// `code` is the folder name written to disk, so it stays lowercase and stable;
// `label` is only ever displayed. Adding a camera later means adding a row
// here — nothing on the server hard-codes this list.
const DEVICES = [
  { code: 'sony', label: 'Sony', Icon: PhotoCameraIcon },
  { code: 'fujifilm', label: 'Fujifilm', Icon: CameraIcon },
  { code: 'iphone', label: 'iPhone', Icon: PhoneIphoneIcon },
  { code: 'gopro', label: 'GoPro', Icon: VideoCameraBackIcon },
  { code: 'drone', label: 'Drone', Icon: FlightIcon },
]

const DEVICE_BY_CODE = DEVICES.reduce((m, d) => { m[d.code] = d; return m }, {})

// Folders can also appear from outside the app — an SMB card dump, a manual
// mkdir — carrying any name at all. Those still render, under their raw folder
// name with a neutral icon, rather than disappearing from the library.
function deviceMeta(code) {
  const key = String(code || '').toLowerCase()
  return DEVICE_BY_CODE[key] || { code: key, label: code || 'Unknown', Icon: DevicesOtherIcon }
}

// Rewrite the server-baked media origin to the client's image host — same
// helper as MediaView. Path and query are preserved verbatim: the signature
// lives in the query string, so stripping it would make every URL 403.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI

function storeUrl(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch { return url }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// "2026-07-17" → "17 Jul 2026"
function prettyDate(d) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return d
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Two modes, one component. Normally this browses the originals and attaches
// a selection to a project. In `pickMode` the same selection UI is used to
// group originals under an open shoot instead — the grid, the device headers
// and the select-all behaviour are identical, only the bottom bar differs.
export default function MediaLibrary({
  username,
  onBack,
  setIsVisible,
  pickMode = false,
  pickLabel = '',
  onPick,
}) {
  const { data, loading, refetch } = useQuery(GET_MEDIA_LIBRARY_QUERY, {
    variables: { username },
    skip: !username,
    fetchPolicy: 'cache-and-network',
  })
  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const [attachAssets, { loading: attaching }] = useMutation(ATTACH_MEDIA_ASSETS_MUTATION)
  const [createUploadGrant] = useMutation(CREATE_UPLOAD_GRANT_MUTATION)

  const days = useMemo(() => data?.getMediaLibrary || [], [data])
  const existingProjects = projectsData?.getProjects || []

  const [selected, setSelected] = useState(new Set())
  const [attachProject, setAttachProject] = useState('')

  // Import panel: upload from anywhere into _inbox/, which the server then
  // sorts into _library/<capture date>/<device>/ by reading each file.
  const [importOpen, setImportOpen] = useState(false)
  const [importDevice, setImportDevice] = useState('')
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState(null)
  const importInputRef = useRef(null)

  // Signed permission to write into one folder, refreshed when nearly spent.
  // The image-server no longer accepts a shared token, so an import can't
  // start without one. See the identical helper in MediaView.
  const grantRef = useRef(null)
  const getUploadUrl = useCallback(async (rel) => {
    const cached = grantRef.current
    if (cached && cached.path === rel && cached.expiresAtMs - Date.now() > 120000) {
      return cached.url
    }
    const { data } = await createUploadGrant({ variables: { path: rel } })
    const grant = data?.createUploadGrant
    if (!grant?.url) throw new Error('Could not get permission to upload here')
    grantRef.current = {
      path: rel,
      url: storeUrl(grant.url),
      expiresAtMs: new Date(grant.expiresAt).getTime(),
    }
    return grantRef.current.url
  }, [createUploadGrant])

  const handleImport = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    if (!IMAGE_STORE_URI) { window.alert('REACT_APP_IMAGE_STORE_URI is not set — cannot reach the media server.'); return }

    // Everything lands in the inbox first; the server then reads each file's
    // capture date and camera and files it into the library. Nothing here
    // decides where a file belongs — the file itself does.
    const rel = INBOX_PATH

    setImporting(true)
    let ok = 0
    const failed = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      const label = `${i + 1}/${files.length} — ${f.name}`
      setImportMsg(`Uploading ${label}…`)
      try {
        // uploadFile slices anything large into sub-100 MB requests, so the
        // tunnel's per-request cap no longer limits file size. There is no
        // size pre-flight any more — a 7 GB clip is simply more chunks.
        await uploadFile(f, () => getUploadUrl(rel), (frac) => {
          setImportMsg(
            f.size > CHUNK_THRESHOLD && frac < 1
              ? `Uploading ${label} — ${Math.round(frac * 100)}%`
              : `Uploading ${label}…`
          )
        })
        ok++
      } catch (e) {
        const m = String(e.message)
        failed.push(
          `${f.name} — ` +
          (m.includes('Load failed') || m.includes('Failed to fetch')
            ? 'connection lost — the server may be unreachable'
            : m)
        )
      }
    }

    if (importInputRef.current) importInputRef.current.value = ''

    // Now file what landed: hash, read capture date and camera, deduplicate,
    // move into the library. Reported before refreshing, because a refetch
    // that throws used to take the result message with it and leave the panel
    // frozen on the last "Uploading n/n" line.
    let summary = `Uploaded ${ok}/${files.length}`
    if (ok > 0) {
      setImportMsg(`Uploaded ${ok} — sorting by capture date…`)
      try {
        const r = await runIngest(await getUploadUrl(rel), importDevice || null)
        const bits = [`filed ${r.filed}`]
        if (r.duplicates) bits.push(`${r.duplicates} already in the library`)
        if (r.errors) bits.push(`${r.errors} error${r.errors === 1 ? '' : 's'}`)
        summary = bits.join(' · ')
      } catch (e) {
        summary = `Uploaded ${ok}, but sorting failed: ${e.message}`
      }
    }
    if (failed.length) summary += ` · ${failed.length} upload${failed.length === 1 ? '' : 's'} failed`
    setImporting(false)
    setImportMsg(summary)

    try {
      await refetch()
    } catch (e) {
      setImportMsg((prev) => `${prev} · couldn't refresh the listing: ${e.message}`)
    }

    if (failed.length > 0) {
      window.alert(`${ok} uploaded, ${failed.length} failed:\n\n${failed.join('\n')}`)
    }
  }, [importDevice, refetch, getUploadUrl])

  const toggle = useCallback((path) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path); else next.add(path)
      return next
    })
  }, [])

  const selectDevice = useCallback((assets) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const allSelected = assets.every((a) => next.has(a.path))
      assets.forEach((a) => { allSelected ? next.delete(a.path) : next.add(a.path) })
      return next
    })
  }, [])

  const clearSelection = () => setSelected(new Set())

  const handleAttach = async () => {
    if (selected.size === 0 || !attachProject.trim()) return
    try {
      await attachAssets({
        variables: { paths: Array.from(selected), project: attachProject.trim(), username },
      })
      clearSelection()
      await refetch()
    } catch (err) {
      console.error('Attach failed:', err)
      window.alert(err.message || 'Failed to attach')
    }
  }

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

      <div className={styles.container} style={{ paddingBottom: selected.size > 0 ? '5rem' : undefined }}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> {pickMode ? 'Cancel' : 'Shoots'}
          </button>
          <div className={styles.titleGroup}>
            <div className={styles.label}>
              {pickMode ? `Pick originals for “${pickLabel}”` : 'Media · originals'}
            </div>
            <h1 className={styles.title}>Library</h1>
          </div>
          <div className={styles.toolbar}>
            <button
              className={`${styles.iconBtn} ${importOpen ? styles.primaryBtn : ''}`}
              title="Import files into the library"
              onClick={() => setImportOpen((v) => !v)}
            >
              <FileUploadIcon fontSize="small" /> Import
            </button>
            <button className={styles.iconBtn} title="Rescan" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {importOpen && (
          <div className={styles.importPanel}>
            <div className={styles.importFields}>
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={importing}
                onClick={() => importInputRef.current && importInputRef.current.click()}
              >
                <FileUploadIcon fontSize="small" /> {importing ? 'Working…' : 'Choose files…'}
              </button>
              <input
                ref={importInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleImport(e.target.files)}
              />
              {/* No date field: the server reads each file's capture date from
                  its EXIF. A card usually spans several days, so picking one
                  date by hand was wrong for most of what was on it. Device is
                  only a fallback for files carrying no usable metadata. */}
              <div className={styles.importField}>
                <span>If a file has no camera info, treat it as</span>
                <div className={styles.devicePicker} role="group" aria-label="Fallback device">
                  {DEVICES.map(({ code, label, Icon }) => {
                    const active = importDevice === code
                    return (
                      <button
                        key={code}
                        type="button"
                        className={`${styles.deviceBtn} ${active ? styles.deviceBtnActive : ''}`}
                        aria-pressed={active}
                        onClick={() => setImportDevice(active ? '' : code)}
                      >
                        <Icon fontSize="small" />
                        <span>{label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className={styles.importHint}>
              Files are sorted automatically by the date they were <strong>taken</strong> and the camera
              that took them — <code>_library/&lt;capture date&gt;/&lt;device&gt;/</code>. Anything already
              in the library is recognised by content and skipped, so re-importing a card is safe.
              Any size: large files upload in parts and resume if interrupted.
              A whole card is still faster over SMB (<code>smb://192.168.1.215</code>) — drop it in
              <code>_inbox/</code> and it files itself.
            </div>
            {importMsg && <div className={styles.importMsg}>{importMsg}</div>}
          </div>
        )}

        {loading && days.length === 0 && <div className={styles.status}>Scanning the library…</div>}

        {!loading && days.length === 0 && (
          <div className={styles.emptyState}>
            <p>No originals in the library yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Offload a card into <code>/Volumes/dataStoreA/media/_library/&lt;date&gt;/&lt;device&gt;/</code> on the mini, then Rescan.
            </p>
          </div>
        )}

        {days.map((day) => (
          <div key={day.date} className={styles.libDay}>
            <div className={styles.libDayHeader}>{prettyDate(day.date)}</div>
            {day.devices.map((dev) => {
              const { label: devLabel, Icon: DevIcon } = deviceMeta(dev.device)
              return (
              <div key={dev.device} className={styles.libDevice}>
                <div className={styles.libDeviceHeader}>
                  <span className={styles.libDeviceName}>
                    <DevIcon fontSize="small" />
                    {devLabel} · {dev.assets.length}
                  </span>
                  <button className={styles.libSelectAll} onClick={() => selectDevice(dev.assets)}>
                    {dev.assets.every((a) => selected.has(a.path)) ? 'deselect all' : 'select all'}
                  </button>
                </div>
                <div className={styles.assetGrid}>
                  {dev.assets.map((a) => {
                    const isSel = selected.has(a.path)
                    return (
                      <div
                        key={a.path}
                        className={`${styles.assetCard} ${isSel ? styles.assetCardSelected : ''}`}
                        onClick={() => toggle(a.path)}
                      >
                        <div className={styles.assetThumb}>
                          {a.thumbnailUrl ? (
                            // See MediaView: a failed load dims the tile, and without this
                            // reset it never brightens again once the thumbnail arrives.
                            <img src={storeUrl(a.thumbnailUrl)} alt={a.filename} loading="lazy"
                              onLoad={(e) => { e.currentTarget.style.opacity = '' }}
                              onError={(e) => { e.currentTarget.style.opacity = 0.2 }} />
                          ) : (
                            <div className={styles.assetThumbPlaceholder}>
                              {a.kind === 'video' ? <VideocamIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                              <span>{a.kind}</span>
                            </div>
                          )}
                          {isSel && <div className={styles.assetCheck}>✓</div>}
                        </div>
                        <div className={styles.assetMeta}>
                          <div className={styles.assetName} title={a.filename}>{a.filename}</div>
                          <div className={styles.assetStats}>{formatBytes(a.size)}</div>
                          {a.projects && a.projects.length > 0 && (
                            <div className={styles.assetProjects}>
                              {a.projects.map((p) => <span key={p} className={styles.assetProjectTag}>{p}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              )
            })}
          </div>
        ))}
      </div>

      {selected.size > 0 && pickMode && (
        <div className={styles.attachBar}>
          <span className={styles.attachCount}>{selected.size} selected</span>
          <span className={styles.attachHint}>
            Grouped under <strong>{pickLabel}</strong> — the files stay in the library.
          </span>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={() => onPick && onPick(Array.from(selected))}
          >
            <LinkIcon fontSize="small" /> Add to shoot
          </button>
          <button className={styles.iconBtn} onClick={clearSelection}>Clear</button>
        </div>
      )}

      {selected.size > 0 && !pickMode && (
        <div className={styles.attachBar}>
          <span className={styles.attachCount}>{selected.size} selected</span>
          {/* A select, not a text field with a datalist: a datalist only
              suggests, so a mistyped name still went through and created a
              near-duplicate project. Media is filed against work that already
              exists; new projects are created in the Work view. */}
          <select
            className={styles.attachSelect}
            value={attachProject}
            onChange={(e) => setAttachProject(e.target.value)}
            aria-label="Attach to project"
          >
            <option value="">
              {existingProjects.length ? 'Attach to project…' : 'No projects yet'}
            </option>
            {existingProjects.map((p) => (
              <option key={p.identity} value={p.properties.title}>
                {p.properties.title}
              </option>
            ))}
          </select>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={handleAttach}
            disabled={attaching || !attachProject}
          >
            <LinkIcon fontSize="small" /> {attaching ? 'Attaching…' : 'Attach'}
          </button>
          <button className={styles.iconBtn} onClick={clearSelection}>Clear</button>
        </div>
      )}
    </div>
  )
}
